const valueOrDash = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    return value;
};

const formatMoney = (value) => {
    if (value === undefined || value === null || value === "") return "-";
    const amount = Number(value);
    if (Number.isNaN(amount)) return value;
    return `${new Intl.NumberFormat("fr-MA", { maximumFractionDigits: 2 }).format(amount)} MAD`;
};

const getNestedLabel = (value, keys = ["description", "name", "libelle", "code"]) => {
    if (!value) return "-";
    if (typeof value !== "object") return value;
    for (const key of keys) {
        if (value[key]) return value[key];
    }
    return value.id || "-";
};

const sectionRows = (rows) => rows.map(([label, value]) => `
    <tr>
        <th>${label}</th>
        <td>${valueOrDash(value)}</td>
    </tr>
`).join("");

const escapeHtml = (value) => String(valueOrDash(value))
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");

export const printVehicleRecap = (vehicle = {}) => {
    const title = [vehicle.marque, vehicle.modele, vehicle.type].filter(Boolean).join(" ") || vehicle.numChassis || "Vehicule";
    const today = new Date().toLocaleDateString("fr-FR");

    const sections = [
        {
            title: "Identification",
            rows: [
                ["Marque", vehicle.marque],
                ["Modele", vehicle.modele],
                ["Type", vehicle.type],
                ["Genre", vehicle.genre],
                ["Carburant", vehicle.carburant],
                ["N chassis", vehicle.numChassis],
                ["Immatriculation", vehicle.immatriculation],
                ["Immat. anterieure", vehicle.immanterieur],
            ],
        },
        {
            title: "Dates et usage",
            rows: [
                ["1ere mise en circulation", vehicle.firstMec],
                ["MEC Maroc", vehicle.mecMaroc],
                ["Date sinistre", vehicle.dateSinistre],
                ["Age", `${valueOrDash(vehicle.ageYears)} an(s), ${valueOrDash(vehicle.ageMonths)} mois`],
                ["Usage", vehicle.usage],
                ["Compteur", vehicle.compteur ? `${vehicle.compteur} km` : "-"],
            ],
        },
        {
            title: "Administratif",
            rows: [
                ["Proprietaire", vehicle.proprietere],
                ["Ville", getNestedLabel(vehicle.ville)],
                ["Assurance", getNestedLabel(vehicle.assurance)],
                ["Prestataire", getNestedLabel(vehicle.prestataire, ["prestataire", "description", "name"])],
                ["Entretien regulier", vehicle.entretReg],
                ["Reforme", vehicle.reform],
            ],
        },
        {
            title: "Valeurs",
            rows: [
                ["Prix achat", formatMoney(vehicle.prixAchat)],
                ["Valeur marche", formatMoney(vehicle.valMarche)],
                ["Epave", formatMoney(vehicle.epave)],
                ["Devis", formatMoney(vehicle.devis)],
                ["Reduction", formatMoney(vehicle.remise)],
                ["Frais immatriculation", formatMoney(vehicle.fraisimm)],
                ["Coefficient commercial", vehicle.coefCommercial],
            ],
        },
    ];

    const html = `
<!doctype html>
<html>
<head>
    <title>Recap vehicule - ${escapeHtml(title)}</title>
    <style>
        body { font-family: Arial, sans-serif; color: #172033; margin: 32px; }
        .header { border-bottom: 2px solid #0f766e; padding-bottom: 16px; margin-bottom: 24px; }
        .kicker { color: #0f766e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
        h1 { font-size: 24px; margin: 8px 0 4px; }
        .meta { color: #657287; font-size: 12px; }
        .grid { display: grid; grid-template-columns: 1fr 1fr; gap: 18px; }
        .section { border: 1px solid #dde5f0; border-radius: 6px; overflow: hidden; break-inside: avoid; }
        .section h2 { background: #f3f6fb; font-size: 15px; margin: 0; padding: 10px 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { text-align: left; padding: 8px 12px; border-top: 1px solid #eef2f7; font-size: 12px; vertical-align: top; }
        th { width: 42%; color: #657287; font-weight: 700; }
        @media print { body { margin: 18mm; } .grid { gap: 12px; } }
    </style>
</head>
<body>
    <div class="header">
        <div class="kicker">Fiche vehicule</div>
        <h1>${escapeHtml(title)}</h1>
        <div class="meta">Genere le ${today}</div>
    </div>
    <div class="grid">
        ${sections.map(section => `
            <div class="section">
                <h2>${escapeHtml(section.title)}</h2>
                <table>${sectionRows(section.rows.map(([label, value]) => [escapeHtml(label), escapeHtml(value)]))}</table>
            </div>
        `).join("")}
    </div>
</body>
</html>`;

    const printWindow = window.open("", "_blank", "width=1000,height=800");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
};

export const printVehiclesList = (vehicles = []) => {
    const today = new Date().toLocaleDateString("fr-FR");
    const rows = vehicles.map((vehicle, index) => `
        <tr>
            <td>${index + 1}</td>
            <td>${escapeHtml([vehicle.marque, vehicle.modele, vehicle.type].filter(Boolean).join(" ") || "-")}</td>
            <td>${escapeHtml(vehicle.numChassis)}</td>
            <td>${escapeHtml(vehicle.immatriculation)}</td>
            <td>${escapeHtml(vehicle.firstMec)}</td>
            <td>${escapeHtml(vehicle.proprietere)}</td>
            <td>${escapeHtml(formatMoney(vehicle.prixAchat))}</td>
            <td>${escapeHtml(vehicle.reform)}</td>
        </tr>
    `).join("");

    const html = `
<!doctype html>
<html>
<head>
    <title>Liste des vehicules</title>
    <style>
        body { font-family: Arial, sans-serif; color: #172033; margin: 28px; }
        .header { border-bottom: 2px solid #0f766e; padding-bottom: 14px; margin-bottom: 20px; }
        .kicker { color: #0f766e; font-size: 12px; font-weight: 700; text-transform: uppercase; }
        h1 { font-size: 22px; margin: 7px 0 4px; }
        .meta { color: #657287; font-size: 12px; }
        table { width: 100%; border-collapse: collapse; }
        th, td { text-align: left; padding: 8px; border: 1px solid #dde5f0; font-size: 11px; vertical-align: top; }
        th { background: #f3f6fb; color: #25324a; }
        @media print { body { margin: 12mm; } }
    </style>
</head>
<body>
    <div class="header">
        <div class="kicker">Parc vehicules</div>
        <h1>Liste des vehicules</h1>
        <div class="meta">Generee le ${today} - ${vehicles.length} vehicule(s)</div>
    </div>
    <table>
        <thead>
            <tr>
                <th>#</th>
                <th>Vehicule</th>
                <th>N chassis</th>
                <th>Immatriculation</th>
                <th>1ere MEC</th>
                <th>Proprietaire</th>
                <th>Prix achat</th>
                <th>Reforme</th>
            </tr>
        </thead>
        <tbody>${rows || `<tr><td colspan="8">Aucun vehicule a imprimer.</td></tr>`}</tbody>
    </table>
</body>
</html>`;

    const printWindow = window.open("", "_blank", "width=1100,height=800");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
};
